import { isFlowSocket, SOCKET_NOT_TAKEN } from "./types";
import { detectCycles } from "./validation/graph-validation";
import { areSocketTypesCompatible } from "./validation/socket-compatibility";

export type WorkflowExpressionAst =
  | { kind: "string"; value: string }
  | { kind: "number"; value: number }
  | { kind: "call"; name: "output" | "concat"; args: WorkflowExpressionAst[] };

export interface WorkflowExpressionReference {
  stepId: string;
  socketId: string;
}

export interface WorkflowExpressionEdge {
  sourceStepId: string;
  sourceSocketId: string;
  targetStepId: string;
  targetSocketId: string;
}

export interface WorkflowExpressionSocket {
  id: string;
  /** Defaults to "any" when omitted. */
  type?: string;
  required?: boolean;
  expression?: string;
}

export interface WorkflowExpressionStep {
  id: string;
  inputSockets: WorkflowExpressionSocket[];
  outputSockets: Array<{ id: string; type?: string }>;
}

export interface WorkflowExpressionValidationError {
  code:
    | "INVALID_EXPRESSION"
    | "UNKNOWN_STEP"
    | "UNKNOWN_SOCKET"
    | "INCOMPATIBLE_TYPE"
    | "CYCLE_DETECTED";
  message: string;
  stepId?: string;
  socketId?: string;
}

type Token =
  | { kind: "paren"; value: "(" | ")" }
  | { kind: "string"; value: string }
  | { kind: "number"; value: number }
  | { kind: "symbol"; value: string };

interface ParseState {
  tokens: Token[];
  index: number;
}

function tokenizeExpression(expression: string): Token[] {
  const tokens: Token[] = [];
  let index = 0;

  while (index < expression.length) {
    const char = expression[index];
    if (/\s/.test(char)) {
      index += 1;
      continue;
    }
    if (char === "(" || char === ")") {
      tokens.push({ kind: "paren", value: char });
      index += 1;
      continue;
    }
    if (char === '"') {
      let value = "";
      let closed = false;
      index += 1;
      while (index < expression.length) {
        const current = expression[index];
        if (current === '"') {
          index += 1;
          tokens.push({ kind: "string", value });
          closed = true;
          break;
        }
        if (current === "\\") {
          const next = expression[index + 1];
          if (next === undefined) {
            throw new Error("Unterminated escape sequence");
          }
          if (next === '"' || next === "\\" || next === "n" || next === "t") {
            value += next === "n" ? "\n" : next === "t" ? "\t" : next;
            index += 2;
            continue;
          }
          throw new Error(`Unsupported escape sequence \\${next}`);
        }
        value += current;
        index += 1;
      }
      if (!closed) {
        throw new Error("Unterminated string literal");
      }
      continue;
    }

    const start = index;
    while (
      index < expression.length &&
      !/\s/.test(expression[index]) &&
      expression[index] !== "(" &&
      expression[index] !== ")"
    ) {
      index += 1;
    }
    const raw = expression.slice(start, index);
    if (/^-?\d+(\.\d+)?$/.test(raw)) {
      tokens.push({ kind: "number", value: Number(raw) });
    } else {
      tokens.push({ kind: "symbol", value: raw });
    }
  }

  return tokens;
}

function parseNode(state: ParseState): WorkflowExpressionAst {
  const token = state.tokens[state.index];
  if (!token) {
    throw new Error("Unexpected end of expression");
  }

  state.index += 1;

  if (token.kind === "string") {
    return { kind: "string", value: token.value };
  }

  if (token.kind === "number") {
    return { kind: "number", value: token.value };
  }

  if (token.kind === "symbol") {
    throw new Error(`Unexpected bare symbol "${token.value}"`);
  }

  if (token.value === ")") {
    throw new Error("Unexpected closing parenthesis");
  }

  const callee = state.tokens[state.index];
  if (!callee || callee.kind !== "symbol") {
    throw new Error("Expected function name");
  }
  state.index += 1;

  if (callee.value !== "output" && callee.value !== "concat") {
    throw new Error(`Unknown function "${callee.value}"`);
  }

  const args: WorkflowExpressionAst[] = [];
  while (state.index < state.tokens.length) {
    const next = state.tokens[state.index];
    if (next.kind === "paren" && next.value === ")") {
      state.index += 1;
      const ast: WorkflowExpressionAst = {
        kind: "call",
        name: callee.value,
        args,
      };
      validateArity(ast);
      return ast;
    }
    args.push(parseNode(state));
  }

  throw new Error("Missing closing parenthesis");
}

function validateArity(ast: WorkflowExpressionAst): void {
  if (ast.kind !== "call") return;
  if (ast.name === "output") {
    if (ast.args.length !== 2) {
      throw new Error("output expects exactly 2 arguments");
    }
    if (ast.args.some((arg) => arg.kind !== "string")) {
      throw new Error("output arguments must be string literals");
    }
  }
  if (ast.name === "concat" && ast.args.length < 1) {
    throw new Error("concat expects at least 1 argument");
  }
}

export function parseWorkflowExpression(
  expression: string,
): WorkflowExpressionAst {
  const tokens = tokenizeExpression(expression);
  if (tokens.length === 0) {
    throw new Error("Expression is empty");
  }
  const state: ParseState = { tokens, index: 0 };
  const ast = parseNode(state);
  if (state.index !== tokens.length) {
    throw new Error("Unexpected tokens after expression");
  }
  return ast;
}

export function formatOutputExpression(
  stepId: string,
  socketId: string,
): string {
  return `(output ${JSON.stringify(stepId)} ${JSON.stringify(socketId)})`;
}

export function formatConcatExpression(
  expressions: string[],
): string | undefined {
  if (expressions.length === 0) return undefined;
  if (expressions.length === 1) return expressions[0];
  return `(concat ${expressions.join(" ")})`;
}

/**
 * Build per-socket expressions from edges (the dual-write encoding).
 * Flow-control edges are skipped; fan-in (multiple edges into one socket)
 * becomes a flat `(concat ...)`.
 *
 * Returns a map keyed `${targetStepId}:${targetSocketId}` → expression.
 */
export function buildSocketExpressionsFromEdges(
  edges: Array<{
    sourceStepId: string;
    sourceSocketId: string;
    targetStepId: string;
    targetSocketId: string;
  }>,
): Map<string, string> {
  const expressionsByTarget = new Map<string, string[]>();
  for (const edge of edges) {
    if (isFlowSocket(edge.targetSocketId)) continue;
    const key = `${edge.targetStepId}:${edge.targetSocketId}`;
    const expression = formatOutputExpression(
      edge.sourceStepId,
      edge.sourceSocketId,
    );
    const existing = expressionsByTarget.get(key);
    if (existing) {
      existing.push(expression);
    } else {
      expressionsByTarget.set(key, [expression]);
    }
  }

  const result = new Map<string, string>();
  for (const [key, expressions] of expressionsByTarget) {
    const combined = formatConcatExpression(expressions);
    if (combined) result.set(key, combined);
  }
  return result;
}

function extractReferencesFromAst(
  ast: WorkflowExpressionAst,
): WorkflowExpressionReference[] {
  const refs: WorkflowExpressionReference[] = [];

  const visit = (node: WorkflowExpressionAst) => {
    if (node.kind !== "call") return;
    if (node.name === "output") {
      const [stepArg, socketArg] = node.args;
      if (stepArg.kind === "string" && socketArg.kind === "string") {
        refs.push({ stepId: stepArg.value, socketId: socketArg.value });
      }
    }
    for (const arg of node.args) {
      visit(arg);
    }
  };

  visit(ast);
  return refs;
}

export function extractWorkflowExpressionReferences(
  expression: string,
): WorkflowExpressionReference[] {
  return extractReferencesFromAst(parseWorkflowExpression(expression));
}

function evaluateAst(
  ast: WorkflowExpressionAst,
  context: Record<string, Record<string, unknown>>,
): unknown {
  const evaluate = (node: WorkflowExpressionAst): unknown => {
    if (node.kind === "string" || node.kind === "number") {
      return node.value;
    }
    if (node.name === "output") {
      const [stepArg, socketArg] = node.args;
      if (stepArg.kind !== "string" || socketArg.kind !== "string") {
        throw new Error("output arguments must be string literals");
      }
      return context[stepArg.value]?.[socketArg.value];
    }
    const values = node.args.map(evaluate);
    return values.flatMap((value) => {
      // Drop dead-branch sentinels per-argument, mirroring edge resolution
      // (collectStepInputs / isEdgeEmpty in graphTraversal.ts). Without this, a
      // fan-in concat over one live and one pruned upstream yields
      // [value, SOCKET_NOT_TAKEN] instead of [value] — corrupting the
      // downstream input under expression-primary resolution and spuriously
      // tripping the edge-vs-expression dual-read mismatch warning.
      if (value == null || value === SOCKET_NOT_TAKEN) return [];
      if (Array.isArray(value)) {
        return value.filter((item) => item !== SOCKET_NOT_TAKEN);
      }
      return [value];
    });
  };

  return evaluate(ast);
}

export function evaluateWorkflowExpression(
  expression: string,
  context: Record<string, Record<string, unknown>>,
): unknown {
  return evaluateAst(parseWorkflowExpression(expression), context);
}

export function evaluateWorkflowExpressionWithRefs(
  expression: string,
  context: Record<string, Record<string, unknown>>,
): { value: unknown; refs: WorkflowExpressionReference[] } {
  const ast = parseWorkflowExpression(expression);
  return {
    value: evaluateAst(ast, context),
    refs: extractReferencesFromAst(ast),
  };
}

export function deriveWorkflowExpressionEdges(
  steps: Array<{
    id: string;
    inputSockets: Array<{ id: string; expression?: string }>;
  }>,
): WorkflowExpressionEdge[] {
  const edges: WorkflowExpressionEdge[] = [];
  for (const step of steps) {
    for (const socket of step.inputSockets) {
      if (!socket.expression) continue;
      for (const ref of extractWorkflowExpressionReferences(
        socket.expression,
      )) {
        edges.push({
          sourceStepId: ref.stepId,
          sourceSocketId: ref.socketId,
          targetStepId: step.id,
          targetSocketId: socket.id,
        });
      }
    }
  }
  return edges;
}

export function remapWorkflowExpressionStepIds(
  expression: string,
  stepIdMap: ReadonlyMap<string, string>,
): string {
  const ast = parseWorkflowExpression(expression);

  const format = (node: WorkflowExpressionAst): string => {
    if (node.kind === "number") return String(node.value);
    if (node.kind === "string") return JSON.stringify(node.value);
    if (node.name === "output") {
      const [stepArg, socketArg] = node.args;
      if (stepArg.kind !== "string" || socketArg.kind !== "string") {
        throw new Error("output arguments must be string literals");
      }
      return formatOutputExpression(
        stepIdMap.get(stepArg.value) ?? stepArg.value,
        socketArg.value,
      );
    }
    return `(concat ${node.args.map(format).join(" ")})`;
  };

  return format(ast);
}

function expressionResultType(
  ast: WorkflowExpressionAst,
  stepsById: ReadonlyMap<string, WorkflowExpressionStep>,
): string | undefined {
  if (ast.kind === "string") return "string";
  if (ast.kind === "number") return "number";
  if (ast.name === "concat") return undefined;

  const [stepArg, socketArg] = ast.args;
  if (stepArg.kind !== "string" || socketArg.kind !== "string") {
    return undefined;
  }
  return stepsById
    .get(stepArg.value)
    ?.outputSockets.find((socket) => socket.id === socketArg.value)?.type;
}

export function validateWorkflowExpressions(
  steps: WorkflowExpressionStep[],
): WorkflowExpressionValidationError[] {
  const errors: WorkflowExpressionValidationError[] = [];
  const stepsById = new Map(steps.map((step) => [step.id, step]));
  const dependencyEdges: WorkflowExpressionEdge[] = [];

  for (const step of steps) {
    for (const inputSocket of step.inputSockets) {
      if (!inputSocket.expression) continue;

      let ast: WorkflowExpressionAst;
      try {
        ast = parseWorkflowExpression(inputSocket.expression);
      } catch (error) {
        errors.push({
          code: "INVALID_EXPRESSION",
          message: error instanceof Error ? error.message : String(error),
          stepId: step.id,
          socketId: inputSocket.id,
        });
        continue;
      }

      for (const ref of extractReferencesFromAst(ast)) {
        const sourceStep = stepsById.get(ref.stepId);
        if (!sourceStep) {
          errors.push({
            code: "UNKNOWN_STEP",
            message: `Expression references unknown step "${ref.stepId}"`,
            stepId: step.id,
            socketId: inputSocket.id,
          });
          continue;
        }
        if (
          !sourceStep.outputSockets.some((socket) => socket.id === ref.socketId)
        ) {
          errors.push({
            code: "UNKNOWN_SOCKET",
            message: `Expression references unknown output socket "${ref.socketId}"`,
            stepId: step.id,
            socketId: inputSocket.id,
          });
        }
        dependencyEdges.push({
          sourceStepId: ref.stepId,
          sourceSocketId: ref.socketId,
          targetStepId: step.id,
          targetSocketId: inputSocket.id,
        });
      }

      const resultType = expressionResultType(ast, stepsById);
      if (
        resultType &&
        !areSocketTypesCompatible(resultType, inputSocket.type ?? "any")
      ) {
        errors.push({
          code: "INCOMPATIBLE_TYPE",
          message: `Expression type "${resultType}" is not compatible with input socket type "${inputSocket.type}"`,
          stepId: step.id,
          socketId: inputSocket.id,
        });
      }
    }
  }

  const cycleResult = detectCycles(steps, dependencyEdges);
  if (cycleResult.hasCycle) {
    errors.push({
      code: "CYCLE_DETECTED",
      message: "Expression dependencies contain a cycle",
      stepId: cycleResult.cycleNodeIds?.[0],
    });
  }

  return errors;
}
